import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Send, CheckCheck, Eye, XCircle, MessagesSquare, Users2, Workflow, Coins,
  Smartphone, CheckCircle2, Circle, Server, Database, Webhook, Clock,
  TrendingUp, ArrowUpRight,
} from "lucide-react";

const CHART_COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#8b5cf6", "#ec4899"];

function greeting() {
  const h = new Date().getHours();
  if (h < 13) return "Buongiorno";
  if (h < 18) return "Buon pomeriggio";
  return "Buonasera";
}

const KPI_DEFS = [
  { key: "messages_today", label: "Messaggi oggi", icon: Send, color: "text-emerald-500" },
  { key: "messages_month", label: "Messaggi (mese)", icon: TrendingUp, color: "text-blue-500" },
  { key: "messages_delivered", label: "Consegnati", icon: CheckCheck, color: "text-emerald-500" },
  { key: "messages_read", label: "Letti", icon: Eye, color: "text-indigo-500" },
  { key: "messages_failed", label: "Falliti", icon: XCircle, color: "text-red-500" },
  { key: "conversations_open", label: "Conversazioni aperte", icon: MessagesSquare, color: "text-amber-500" },
  { key: "contacts_total", label: "Contatti totali", icon: Users2, color: "text-blue-500" },
  { key: "automations_active", label: "Automazioni attive", icon: Workflow, color: "text-violet-500" },
  { key: "credits_remaining", label: "Crediti residui", icon: Coins, color: "text-amber-500" },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data.data,
  });

  if (isLoading || !data) return <DashboardSkeleton />;

  const kpis = data.kpis || {};
  const activities = data.activities || [];
  const checklist = data.checklist || [];
  const checklist_progress = data.checklist_progress || 0;
  const services = data.services || {};
  const rawCharts = data.charts || {};
  const charts = {
    messages_7d: rawCharts.messages_7d || [],
    messages_30d: rawCharts.messages_30d || [],
    delivery_rate_7d: rawCharts.delivery_rate_7d || [],
    read_rate_7d: rawCharts.read_rate_7d || [],
    category_distribution: rawCharts.category_distribution || [],
    status_distribution: rawCharts.status_distribution || [],
    contacts_growth: rawCharts.contacts_growth || [],
  };
  const hasCharts = charts.messages_7d.length > 0;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-6 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight sm:text-3xl">
            {greeting()}, {data.greeting_name}.
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Ecco cosa sta succedendo su Connectivity · <span className="font-medium text-foreground">{data.tenant?.name}</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary" className="gap-1.5 capitalize">Piano: {data.plan?.code}</Badge>
          {data.data_source && (
            <Badge variant="outline" className={
              data.data_source === "real" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600"
              : data.data_source === "mixed" ? "border-blue-500/40 bg-blue-500/10 text-blue-600"
              : "border-violet-500/40 bg-violet-500/10 text-violet-600"
            } data-testid="data-source-badge">
              {data.data_source === "real" ? "Dati reali" : data.data_source === "mixed" ? "Dati misti" : "Dati demo"}
            </Badge>
          )}
          <Badge variant="outline" className="gap-1.5">
            <Smartphone className="h-3.5 w-3.5" />
            WhatsApp: {data.whatsapp_status === "connected" ? "Collegato" : "Non collegato"}
          </Badge>
        </div>
      </div>

      {/* Onboarding progress banner */}
      {data.onboarding_progress < 100 && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1">
              <p className="font-medium">Completa la configurazione</p>
              <Progress value={data.onboarding_progress} className="mt-2 h-2 max-w-md" />
            </div>
            <Button size="sm" className="rounded-lg" onClick={() => navigate("/onboarding")}>Continua ({data.onboarding_progress}%)</Button>
          </CardContent>
        </Card>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        {KPI_DEFS.map((k) => (
          <Card key={k.key} className="transition-transform hover:-translate-y-0.5" data-testid={`kpi-${k.key}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <k.icon className={`h-5 w-5 ${k.color}`} />
              </div>
              <p className="mt-3 font-heading text-2xl font-semibold tracking-tight">
                {(typeof kpis[k.key] === "number" ? kpis[k.key] : Number(kpis[k.key] || 0)).toLocaleString("it-IT")}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">{k.label}</p>
            </CardContent>
          </Card>
        ))}
        <Card className="bg-primary text-primary-foreground" data-testid="kpi-rates">
          <CardContent className="p-5">
            <p className="text-xs opacity-80">Tassi di performance</p>
            <p className="mt-2 font-heading text-2xl font-semibold">{kpis.delivery_rate || 0}%</p>
            <p className="text-xs opacity-80">consegna</p>
            <p className="mt-1 text-sm font-medium">{kpis.read_rate || 0}% <span className="text-xs opacity-80">lettura</span></p>
          </CardContent>
        </Card>
      </div>

      {/* Charts row 1 */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2" data-testid="chart-messages-7d">
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-base font-medium">Andamento messaggi (7 giorni)</CardTitle>
            <Badge variant="secondary" className="gap-1"><ArrowUpRight className="h-3 w-3" /> +12%</Badge>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={charts.messages_7d}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
                <Area type="monotone" dataKey="inviati" stroke="#10b981" strokeWidth={2} fill="url(#g1)" />
                <Area type="monotone" dataKey="consegnati" stroke="#3b82f6" strokeWidth={2} fillOpacity={0} />
                <Area type="monotone" dataKey="letti" stroke="#8b5cf6" strokeWidth={2} fillOpacity={0} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card data-testid="chart-category">
          <CardHeader><CardTitle className="text-base font-medium">Distribuzione per categoria</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={charts.category_distribution} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {charts.category_distribution.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-2 grid grid-cols-2 gap-1.5">
              {charts.category_distribution.map((e, i) => (
                <div key={i} className="flex items-center gap-1.5 text-xs">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <span className="text-muted-foreground">{e.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts row 2 */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card data-testid="chart-delivery">
          <CardHeader><CardTitle className="text-base font-medium">Tasso di consegna</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={charts.delivery_rate_7d}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis domain={[90, 100]} stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
                <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card data-testid="chart-read">
          <CardHeader><CardTitle className="text-base font-medium">Tasso di lettura</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={charts.read_rate_7d}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis domain={[70, 90]} stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
                <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card data-testid="chart-growth">
          <CardHeader><CardTitle className="text-base font-medium">Crescita contatti</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={charts.contacts_growth}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }} />
                <Bar dataKey="value" fill="#10b981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Bottom row: activities, checklist, services */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1" data-testid="activities-card">
          <CardHeader><CardTitle className="text-base font-medium">Attività recenti</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {activities.slice(0, 8).map((a) => (
              <div key={a.id} className="flex gap-3">
                <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />
                <div className="flex-1 border-b border-border/50 pb-3">
                  <p className="text-sm">{a.description}</p>
                  <p className="text-xs text-muted-foreground">{a.user_name} · {new Date(a.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card data-testid="checklist-card">
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-base font-medium">Completa la configurazione</CardTitle>
            <span className="text-sm font-semibold text-primary">{checklist_progress}%</span>
          </CardHeader>
          <CardContent className="space-y-2.5">
            {checklist.map((c) => (
              <div key={c.key} className="flex items-center gap-2.5">
                {c.done ? <CheckCircle2 className="h-4 w-4 text-primary" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
                <span className={`text-sm ${c.done ? "text-muted-foreground line-through" : ""}`}>{c.label}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card data-testid="services-card">
          <CardHeader><CardTitle className="text-base font-medium">Stato servizi</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <ServiceRow icon={Server} label="Backend" status={services.backend} />
            <ServiceRow icon={Database} label="Database" status={services.database} />
            <ServiceRow icon={Webhook} label="Webhook" status={services.webhooks} />
            <ServiceRow icon={Smartphone} label="WhatsApp" status={services.whatsapp} />
            <div className="flex items-center justify-between border-t border-border pt-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Uptime</span>
              <span className="font-medium text-foreground">{services.uptime}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ServiceRow({ icon: Icon, label, status }) {
  const ok = status === "operational" || status === "connected";
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-sm"><Icon className="h-4 w-4 text-muted-foreground" /> {label}</span>
      <Badge variant="outline" className={`gap-1.5 ${ok ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600" : "border-amber-500/40 bg-amber-500/10 text-amber-600"}`}>
        <span className={`h-1.5 w-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-amber-500"}`} />
        {ok ? "Operativo" : "Non collegato"}
      </Badge>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-24 w-full rounded-2xl" />
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Skeleton className="h-80 rounded-xl lg:col-span-2" />
        <Skeleton className="h-80 rounded-xl" />
      </div>
    </div>
  );
}
